import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { clientLogin as apiClientLogin, getClientInvoices, updateInvoiceStatus as apiUpdateStatus, clientChangePassword as apiClientChangePassword, getNotificationSummary as apiGetNotificationSummary } from '../services/api';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [currentView, setCurrentView] = useState('public');
  const [portalUser, setPortalUser] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notificationSummary, setNotificationSummary] = useState({ messagesUnread: 0, documentsNew: 0, documentsTotal: 0, reviewRequestsUnread: 0, reviewsPending: 0 });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedClient = localStorage.getItem('client');
    if (token && savedClient) {
      try {
        const client = JSON.parse(savedClient);
        // Restore client-facing keys used across the portal (backwards compatibility)
        localStorage.setItem('clientToken', token);
        localStorage.setItem('clientId', client.id || client._id);
        setPortalUser(client);
        setCurrentView('portal');
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('client');
        localStorage.removeItem('clientToken');
        localStorage.removeItem('clientId');
      }
    }
  }, []);

  const clientLogin = async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const params = typeof payload === 'string' ? { email: payload } : (payload || {});
      const data = await apiClientLogin(params);
      localStorage.setItem('token', data.token);
      // Backwards-compatible keys used elsewhere in the portal
      localStorage.setItem('clientToken', data.token);
      localStorage.setItem('clientId', data.client.id || data.client._id);
      localStorage.setItem('client', JSON.stringify(data.client));
      setPortalUser(data.client);
      setCurrentView('portal');
      return data;
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('client');
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientId');
    setPortalUser(null);
    setCurrentView('public');
  };

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getClientInvoices();
      setInvoices(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  const clientChangePassword = async (currentPassword, newPassword) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClientChangePassword({ currentPassword, newPassword });
      return data;
    } catch (err) {
      setError(err.response?.data?.error || 'Change password failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateInvoiceStatus = async (id, status) => {
    try {
      const updated = await apiUpdateStatus(id, status);
      setInvoices(prev => prev.map(inv => inv._id === id ? updated : inv));
      return updated;
    } catch (err) {
      throw err;
    }
  };

  const fetchNotificationSummary = useCallback(async (params = {}) => {
    try {
      const data = await apiGetNotificationSummary(params);
      setNotificationSummary({
        messagesUnread: Number(data?.messagesUnread || 0),
        documentsNew: Number(data?.documentsNew || 0),
        documentsTotal: Number(data?.documentsTotal || 0),
        reviewRequestsUnread: Number(data?.reviewRequestsUnread || 0),
        reviewsPending: Number(data?.reviewsPending || 0),
      });
      return data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch notifications');
      return null;
    }
  }, []);

  return (
    <AppContext.Provider value={{
      currentView, setCurrentView, portalUser, clientLogin, logout,
      invoices, fetchInvoices, updateInvoiceStatus, clientChangePassword,
      notificationSummary, fetchNotificationSummary,
      loading, error, setError,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}
